import { useState, useEffect } from 'react';
import { Link, Outlet, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useSubscription } from '../contexts/SubscriptionContext';
import { UserRole } from '@bst-stellar/shared';

const SIDEBAR_COLLAPSED_KEY = 'admin-sidebar-collapsed';

export default function AdminLayout() {
    const { user } = useAuth();
    const { status, loading: subLoading, isReadOnly, isExpired } = useSubscription();
    const location = useLocation();

    const [isCollapsed, setIsCollapsed] = useState(() => {
        const saved = localStorage.getItem(SIDEBAR_COLLAPSED_KEY);
        return saved === 'true';
    });

    useEffect(() => {
        localStorage.setItem(SIDEBAR_COLLAPSED_KEY, String(isCollapsed));
    }, [isCollapsed]);

    // Check if user is admin
    if (!user || (user.role !== UserRole.COMPANY_ADMIN && user.role !== UserRole.DEPT_ADMIN)) {
        return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center">
                <div className="text-center">
                    <h1 className="text-2xl font-bold text-red-600 mb-2">Access Denied</h1>
                    <p className="text-gray-600">You don't have permission to access this page.</p>
                    <Link to="/" className="text-blue-600 hover:underline mt-4 inline-block">
                        Go to Dashboard
                    </Link>
                </div>
            </div>
        );
    }

    const navItems = [
        { path: '/admin', label: 'Dashboard', icon: '📊' },
        { path: '/admin/claims', label: 'Validate Claims', icon: '✅' },
        { path: '/admin/games', label: 'Games', icon: '🎮' },
        { path: '/admin/employees', label: 'Employees', icon: '👥' },
        { path: '/admin/invitations', label: 'Invitations', icon: '✉️' },
        { path: '/admin/departments', label: 'Departments', icon: '🏢' },
        { path: '/admin/items', label: 'Items', icon: '📝' },
        { path: '/admin/tips', label: 'Tips', icon: '💡' },
        { path: '/admin/leaves', label: 'Leave Management', icon: '📅' },
        { path: '/admin/branding', label: 'Branding', icon: '🎨' },
        { path: '/admin/slack', label: 'Slack', icon: '💬' },
    ];

    if (user.role === UserRole.COMPANY_ADMIN) {
        navItems.push({ path: '/admin/settings', label: 'Settings', icon: '⚙️' });
    }

    // Status banner colors
    const getStatusBanner = () => {
        if (!status || subLoading) return null;

        const bannerStyles: Record<string, { bg: string; text: string; icon: string }> = {
            trial: { bg: 'bg-blue-500', text: 'text-white', icon: '⏱️' },
            active: { bg: 'bg-green-500', text: 'text-white', icon: '✅' },
            grace: { bg: 'bg-yellow-500', text: 'text-black', icon: '⚠️' },
            readonly: { bg: 'bg-orange-500', text: 'text-white', icon: '🔒' },
            expired: { bg: 'bg-red-600', text: 'text-white', icon: '❌' },
        };

        const style = bannerStyles[status.effectiveStatus] || bannerStyles.trial;

        return (
            <div className={`${style.bg} ${style.text} p-3 rounded-lg mb-4 text-sm`}>
                <div className="flex items-center gap-2 font-medium">
                    <span>{style.icon}</span>
                    <span className="capitalize">{status.effectiveStatus}</span>
                    {status.daysRemaining > 0 && (
                        <span className="ml-auto">{status.daysRemaining}d left</span>
                    )}
                </div>
                {status.message && (
                    <p className="mt-1 text-xs opacity-90">{status.message}</p>
                )}
            </div>
        );
    };

    return (
        <div className="min-h-screen">
            {/* Read-only / Expired Banner */}
            {(isReadOnly || isExpired) && (
                <div className={`${isExpired ? 'bg-red-600' : 'bg-orange-500'} text-white py-2 px-4 text-center text-sm`}>
                    {isExpired
                        ? '⚠️ Your subscription has expired. Contact support to renew and restore full access.'
                        : '🔒 Read-only mode: You cannot make changes until your subscription is renewed.'
                    }
                </div>
            )}

            {/* Top Navigation */}
            <nav className="bg-surface shadow-sm border-b border-border">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex justify-between h-16">
                        <div className="flex items-center">
                            <Link to="/" className="flex items-center text-text-primary hover:text-primary">
                                <span className="text-xl font-bold">← Back to App</span>
                            </Link>
                        </div>
                        <div className="flex items-center">
                            <span className="text-text-secondary mr-4">Admin Console</span>
                            <div className="h-8 w-8 rounded-full bg-primary flex items-center justify-center text-white font-bold">
                                {user?.name?.[0] || 'A'}
                            </div>
                        </div>
                    </div>
                </div>
            </nav>

            <div className="flex">
                {/* Sidebar */}
                <aside className={`${isCollapsed ? 'w-16' : 'w-64'} bg-surface shadow-sm min-h-[calc(100vh-4rem)] border-r border-border flex flex-col transition-all duration-300`}>
                    {!isCollapsed && (
                        <div className="p-4">
                            {getStatusBanner()}
                        </div>
                    )}
                    <nav className="flex-1 px-2 py-4">
                        <div className="space-y-1">
                            {navItems.map((item) => {
                                const isActive = location.pathname === item.path;
                                return (
                                    <Link
                                        key={item.path}
                                        to={item.path}
                                        title={isCollapsed ? item.label : undefined}
                                        className={`group flex items-center px-3 py-2.5 text-base font-medium rounded-lg transition-all duration-200 ${isActive
                                            ? 'bg-primary text-white hover:bg-primary hover:text-white shadow-md'
                                            : 'text-text-secondary hover:bg-background hover:text-text-primary'
                                            } ${isCollapsed ? 'justify-center' : ''}`}
                                    >
                                        <span className={`text-lg flex-shrink-0 ${isCollapsed ? '' : 'mr-3'}`}>{item.icon}</span>
                                        {!isCollapsed && <span className="whitespace-nowrap">{item.label}</span>}
                                    </Link>
                                );
                            })}
                        </div>
                    </nav>

                    {/* Collapse Toggle */}
                    <div className="px-2 py-3 border-t border-border">
                        <button
                            onClick={() => setIsCollapsed(!isCollapsed)}
                            title={isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
                            className="w-full flex items-center justify-center gap-2 px-3 py-2.5 text-sm text-text-secondary hover:bg-background hover:text-text-primary rounded-lg transition-all duration-200 group"
                        >
                            <svg
                                className={`w-5 h-5 transition-transform duration-300 ${isCollapsed ? 'rotate-180' : ''}`}
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                            >
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 19l-7-7 7-7m8 14l-7-7 7-7" />
                            </svg>
                            {!isCollapsed && <span className="font-medium">Collapse</span>}
                        </button>
                    </div>
                </aside>

                {/* Main Content */}
                <main className="flex-1 p-8">
                    <Outlet />
                </main>
            </div>
        </div>
    );
}
